// src/data/mock/donations.ts

/**
 * Donations & Support Data Structure
 * 
 * Donation programs, impact stories, and funding needs.
 * 
 * @note Configure actual payment gateway integration
 * @note Set up real donation tracking system
 */

export interface DonationProgram {
  id: string;
  
  // Program details
  name: {
    en: string;
    ar: string;
  };
  
  description: {
    en: string;
    ar: string;
  };
  
  // Icon
  icon: string;
  
  // Suggested amounts
  suggestedAmounts: number[];
  
  // Minimum amount
  minAmount: number;
  
  // Current goal (if fundraising campaign)
  goal?: number;
  
  // Current raised amount
  raised?: number;
  
  // Impact metrics
  impact: {
    en: string;
    ar: string;
  };
  
  // Category
  category: 'equipment' | 'scholarships' | 'facilities' | 'humanitarian' | 'programs' | 'general';
  
  // Priority level
  priority?: 'urgent' | 'high' | 'medium';
  
  // Is this a recurring donation option?
  allowRecurring?: boolean;
  
  // Tax deductible?
  taxDeductible?: boolean;
}

export interface ImpactStory {
  id: string;
  
  title: {
    en: string;
    ar: string;
  };
  
  story: {
    en: string;
    ar: string;
  };
  
  donor: {
    name: string;
    anonymous?: boolean;
  };
  
  amount?: number;
  
  date: string;
  
  image?: string;
  
  impact: {
    en: string;
    ar: string;
  };
}

export interface PaymentMethod {
  id: string;
  name: string;
  type: 'card' | 'paypal' | 'bank' | 'crypto' | 'mobile';
  icon: string;
  available: boolean;
  description?: {
    en: string;
    ar: string;
  };
}

/**
 * Donation Programs
 */
export const donationPrograms: DonationProgram[] = [
  {
    id: "prog-001",
    category: "scholarships",
    priority: "urgent",
    name: {
      en: "Full Athlete Scholarship",
      ar: "منحة رياضي كاملة"
    },
    description: {
      en: "Provide a full year of training, equipment, and competition fees for one talented athlete from a low-income family.",
      ar: "وفّر سنة كاملة من التدريب والمعدات ورسوم المنافسة لرياضي موهوب من عائلة محدودة الدخل."
    },
    icon: "GraduationCap",
    suggestedAmounts: [500, 750, 1000, 1500],
    minAmount: 250,
    goal: 50000,
    raised: 32000,
    impact: {
      en: "One scholarship supports one athlete for an entire year",
      ar: "منحة واحدة تدعم رياضياً واحداً لسنة كاملة"
    },
    allowRecurring: true,
    taxDeductible: true
  },
  {
    id: "prog-002",
    category: "equipment",
    priority: "high",
    name: {
      en: "Sports Equipment Fund",
      ar: "صندوق المعدات الرياضية"
    },
    description: {
      en: "Purchase essential training equipment including footballs, swimming gear, basketball equipment, and martial arts supplies.",
      ar: "شراء معدات تدريب أساسية تشمل كرات القدم ومعدات السباحة ومعدات كرة السلة ولوازم الفنون القتالية."
    },
    icon: "Target",
    suggestedAmounts: [50, 100, 250, 500],
    minAmount: 25,
    goal: 20000,
    raised: 15000,
    impact: {
      en: "$100 provides equipment for 5 athletes",
      ar: "100$ توفر معدات لـ 5 رياضيين"
    },
    allowRecurring: true,
    taxDeductible: true
  },
  {
    id: "prog-003",
    category: "facilities",
    priority: "high",
    name: {
      en: "Facility Maintenance & Upgrades",
      ar: "صيانة وتطوير المرافق"
    },
    description: {
      en: "Support ongoing maintenance, repairs, and upgrades to our training facilities including swimming pool, courts, and gyms.",
      ar: "دعم الصيانة المستمرة والإصلاحات والترقيات لمرافق التدريب بما في ذلك المسبح والملاعب والصالات الرياضية."
    },
    icon: "Building2",
    suggestedAmounts: [100, 500, 1000, 5000],
    minAmount: 50,
    goal: 100000,
    raised: 45000,
    impact: {
      en: "$1000 covers one month of facility maintenance",
      ar: "1000$ تغطي شهراً من صيانة المرافق"
    },
    allowRecurring: true,
    taxDeductible: true
  },
  {
    id: "prog-004",
    category: "humanitarian",
    priority: "urgent",
    name: {
      en: "Emergency Relief Fund",
      ar: "صندوق الإغاثة الطارئة"
    },
    description: {
      en: "Provide immediate assistance to athletes and their families affected by crisis situations, including food, medical care, and shelter.",
      ar: "تقديم المساعدة الفورية للرياضيين وعائلاتهم المتضررين من الأزمات، بما في ذلك الطعام والرعاية الطبية والمأوى."
    },
    icon: "Hand",
    suggestedAmounts: [25, 50, 100, 250],
    minAmount: 10,
    impact: {
      en: "$50 provides one week of essential supplies for one family",
      ar: "50$ توفر أسبوعاً من الإمدادات الأساسية لعائلة واحدة"
    },
    allowRecurring: false,
    taxDeductible: true
  },
  {
    id: "prog-005",
    category: "programs",
    name: {
      en: "Girls in Sports Initiative",
      ar: "مبادرة الفتيات في الرياضة"
    },
    description: {
      en: "Support our dedicated programs for female athletes, including coaching, facilities, and competition opportunities.",
      ar: "دعم برامجنا المخصصة للرياضيات، بما في ذلك التدريب والمرافق وفرص المنافسة."
    },
    icon: "Users",
    suggestedAmounts: [50, 100, 250, 500],
    minAmount: 25,
    goal: 30000,
    raised: 18000,
    impact: {
      en: "$250 supports one girl for 3 months of training",
      ar: "250$ تدعم فتاة واحدة لـ 3 أشهر من التدريب"
    },
    allowRecurring: true,
    taxDeductible: true
  },
  {
    id: "prog-006",
    category: "general",
    name: {
      en: "General Fund - Where Most Needed",
      ar: "الصندوق العام - حيث تشتد الحاجة"
    },
    description: {
      en: "Your donation will be used where it's needed most to support our athletes, programs, and operations.",
      ar: "سيتم استخدام تبرعك حيث تشتد الحاجة لدعم رياضيينا وبرامجنا وعملياتنا."
    },
    icon: "Heart",
    suggestedAmounts: [25, 50, 100, 250, 500, 1000],
    minAmount: 10,
    impact: {
      en: "Every dollar makes a difference in a young athlete's life",
      ar: "كل دولار يحدث فرقاً في حياة رياضي شاب"
    },
    allowRecurring: true,
    taxDeductible: true
  }
];

/**
 * Impact Stories
 */
export const impactStories: ImpactStory[] = [
  {
    id: "impact-001",
    title: {
      en: "From Struggling to Swimming Champion",
      ar: "من الكفاح إلى بطل السباحة"
    },
    story: {
      en: "Thanks to a scholarship funded by generous donors, 14-year-old Sara went from being unable to afford training to becoming the national junior swimming champion. She now dreams of competing in the Olympics.",
      ar: "بفضل منحة ممولة من متبرعين كرماء، انتقلت سارة البالغة من العمر 14 عاماً من عدم القدرة على تحمل تكاليف التدريب إلى أن أصبحت بطلة السباحة الوطنية للناشئين. تحلم الآن بالمنافسة في الأولمبياد."
    },
    donor: {
      name: "Anonymous Donor",
      anonymous: true
    },
    amount: 1000,
    date: "2023-09-15",
    image: "/assets/images/s-blob-v1-IMAGE-wA5xAwin9fo.jpeg",
    impact: {
      en: "One full scholarship changed her life forever",
      ar: "منحة كاملة واحدة غيرت حياتها للأبد"
    }
  },
  {
    id: "impact-002",
    title: {
      en: "New Equipment, New Opportunities",
      ar: "معدات جديدة، فرص جديدة"
    },
    story: {
      en: "A $500 equipment donation provided 20 young footballers with proper training gear. Within months, 5 of them were scouted by professional academies.",
      ar: "وفر تبرع بمعدات بقيمة 500 دولار لـ 20 لاعب كرة قدم شاب معدات تدريب مناسبة. في غضون أشهر، تم متابعة 5 منهم من قبل أكاديميات احترافية."
    },
    donor: {
      name: "Champions Group",
      anonymous: false
    },
    amount: 500,
    date: "2023-10-20",
    image: "/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg",
    impact: {
      en: "20 athletes equipped, 5 scouted professionally",
      ar: "20 رياضياً مجهزين، 5 تمت متابعتهم احترافياً"
    }
  },
  {
    id: "impact-003",
    title: {
      en: "A Family Saved, A Future Secured",
      ar: "عائلة أُنقذت، مستقبل مضمون"
    },
    story: {
      en: "Emergency relief donations helped the family of one of our top athletes during a crisis. The athlete was able to continue training and just won a regional championship.",
      ar: "ساعدت تبرعات الإغاثة الطارئة عائلة أحد أفضل رياضيينا أثناء أزمة. تمكن الرياضي من مواصلة التدريب وفاز للتو ببطولة إقليمية."
    },
    donor: {
      name: "Community Donors",
      anonymous: false
    },
    date: "2023-11-05",
    image: "/assets/images/s-blob-v1-IMAGE-9MjiX0xpxqY.jpeg",
    impact: {
      en: "One family supported through crisis",
      ar: "عائلة واحدة دُعمت خلال الأزمة"
    }
  }
];

/**
 * Payment Methods
 */
export const paymentMethods: PaymentMethod[] = [
  {
    id: "card",
    name: "Credit/Debit Card",
    type: "card",
    icon: "CreditCard",
    available: true,
    description: {
      en: "Secure payment via Visa, Mastercard, or American Express",
      ar: "دفع آمن عبر فيزا أو ماستركارد أو أمريكان إكسبريس"
    }
  },
  {
    id: "paypal",
    name: "PayPal",
    type: "paypal",
    icon: "Wallet",
    available: true,
    description: {
      en: "Fast and secure payment through PayPal",
      ar: "دفع سريع وآمن عبر باي بال"
    }
  },
  {
    id: "bank",
    name: "Bank Transfer",
    type: "bank",
    icon: "Building2",
    available: true,
    description: {
      en: "Direct bank transfer for larger donations",
      ar: "تحويل بنكي مباشر للتبرعات الكبيرة"
    }
  },
  {
    id: "mobile",
    name: "Mobile Payment",
    type: "mobile",
    icon: "Smartphone",
    available: true,
    description: {
      en: "Pay via mobile wallets",
      ar: "الدفع عبر المحافظ الإلكترونية"
    }
  }
];

/**
 * Donation Impact Statistics
 */
export const donationStats = {
  totalDonors: 1250,
  totalRaised: 250000,
  athletesSupported: 450,
  scholarshipsProvided: 85,
  equipmentSets: 300,
  familiesHelped: 120
};

/**
 * Monthly Giving Tiers
 */
export const monthlyTiers = [
  {
    amount: 25,
    name: { en: "Supporter", ar: "داعم" },
    benefits: {
      en: [
        "Monthly newsletter",
        "Donor recognition on website",
        "Annual impact report"
      ],
      ar: [
        "نشرة إخبارية شهرية",
        "تقدير المتبرع على الموقع",
        "تقرير تأثير سنوي"
      ]
    }
  },
  {
    amount: 50,
    name: { en: "Champion", ar: "بطل" },
    benefits: {
      en: [
        "All Supporter benefits",
        "Quarterly video updates",
        "Invitation to annual event",
        "Champions Academy merchandise"
      ],
      ar: [
        "جميع مزايا الداعم",
        "تحديثات فيديو ربع سنوية",
        "دعوة للحدث السنوي",
        "بضائع أكاديمية تشامبيونز"
      ]
    }
  },
  {
    amount: 100,
    name: { en: "Gold Champion", ar: "بطل ذهبي" },
    benefits: {
      en: [
        "All Champion benefits",
        "Personal athlete connection program",
        "VIP tour of academy facilities",
        "Your name on donor wall",
        "Priority event access"
      ],
      ar: [
        "جميع مزايا البطل",
        "برنامج اتصال شخصي مع رياضي",
        "جولة VIP في مرافق الأكاديمية",
        "اسمك على جدار المتبرعين",
        "وصول أولوية للفعاليات"
      ]
    }
  }
];

/**
 * Utility Functions
 */
export const getProgramsByCategory = (category: DonationProgram['category']) => {
  return donationPrograms.filter(p => p.category === category);
};

export const getUrgentPrograms = () => {
  return donationPrograms.filter(p => p.priority === 'urgent');
};

export const getTotalGoal = () => {
  return donationPrograms.reduce((sum, p) => sum + (p.goal || 0), 0);
};

export const getTotalRaised = () => {
  return donationPrograms.reduce((sum, p) => sum + (p.raised || 0), 0);
};

export const getProgressPercentage = (program: DonationProgram) => {
  if (!program.goal || !program.raised) return 0;
  return Math.min(Math.round((program.raised / program.goal) * 100), 100);
};
